import { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

import { RootState } from '@/redux/store'
import {
  createAdvisorThunk,
  fetchAdvisorsThunk,
} from '@/redux/asyncThunks/advisor'
import { useAppDispatch } from '@/redux/hooks'
import { useParams } from 'react-router-dom'
import { Advisor } from '@/redux/types/advisor'
import { saveAdvisorData } from '@/api/configuration'

interface CreateAdvisorModalProps {
  isOpen: boolean
  onClose: () => void
  onAdvisorCreated?: (advisor: Advisor) => void
  mode: 'create' | 'rename'
  existingAdvisor?: Advisor
}

export function CreateAdvisorModal({
  isOpen,
  onClose,
  onAdvisorCreated,
  mode,
  existingAdvisor,
}: CreateAdvisorModalProps) {
  const [advisorName, setAdvisorName] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const dispatch = useAppDispatch()
  const { loading: storeLoading } = useSelector(
    (state: RootState) => state.advisor
  )
  const { roomId } = useParams()

  useEffect(() => {
    if (isOpen && mode === 'rename' && existingAdvisor) {
      setAdvisorName(existingAdvisor.chatbotname)
    } else if (isOpen && mode === 'create') {
      setAdvisorName('')
    }
    setError(null)
  }, [isOpen, mode, existingAdvisor])

  const handleClose = () => {
    setAdvisorName('')
    setError(null)
    onClose()
  }

  const handleSave = async () => {
    if (!advisorName.trim()) return

    if (mode === 'create') {
      await handleCreate()
    } else {
      await handleRename()
    }
  }

  const handleCreate = async () => {
    try {
      const result = await dispatch(
        createAdvisorThunk({
          advisorData: advisorName.trim(),
          roomId: roomId!,
        })
      )
      if (createAdvisorThunk.fulfilled.match(result)) {
        await dispatch(fetchAdvisorsThunk({ roomId: roomId! }))
        handleClose()
        onAdvisorCreated?.(result.payload)
      }
    } catch (err) {
      setError('Failed to create advisor')
    }
  }

  const handleRename = async () => {
    if (!existingAdvisor) return

    setIsLoading(true)
    setError(null)
    try {
      await saveAdvisorData(existingAdvisor.chatbotid, {
        chatbotname: advisorName.trim(),
      })

      if (roomId) {
        await dispatch(fetchAdvisorsThunk({ roomId }))
      }

      handleClose()
    } catch (err) {
      setError('Failed to rename advisor')
    } finally {
      setIsLoading(false)
    }
  }

  const loading = mode === 'create' ? storeLoading : isLoading

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className='sm:max-w-[425px]'>
        <DialogHeader>
          <div className='flex items-center gap-4'>
            <div className='p-2 rounded-lg bg-secondary'>
              <img src='/icons/aristotle.svg' alt='' className='w-8 h-8' />
            </div>
            <div>
              <DialogTitle>
                {mode === 'create' ? 'New Advisor' : 'Rename Advisor'}
              </DialogTitle>
              <DialogDescription>
                {mode === 'create'
                  ? 'Create an advisor for this game'
                  : 'Enter a new name for the selected advisor'}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        <div className='py-4 space-y-4'>
          <div className='space-y-2'>
            <label htmlFor='advisorName' className='text-sm font-medium'>
              {mode === 'create' ? 'Name of Advisor' : 'Advisor Name'}
            </label>
            <Input
              id='advisorName'
              placeholder={
                mode === 'create' ? 'Enter name here' : 'Enter advisor name'
              }
              value={advisorName}
              onChange={(e) => setAdvisorName(e.target.value)}
              disabled={loading}
            />
          </div>
        </div>
        <div className='flex justify-end gap-2'>
          <Button variant='outline' onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={loading || !advisorName.trim()}
            className='bg-dark-blue hover:bg-dark-blue-hovered'
          >
            {loading
              ? mode === 'create'
                ? 'Creating...'
                : 'Renaming...'
              : mode === 'create'
                ? 'Create'
                : 'Rename'}
          </Button>
        </div>
        {error && <div className='mt-2 text-red-500'>{error}</div>}
      </DialogContent>
    </Dialog>
  )
}
